import { useCallback, useEffect, useRef, useState } from 'react';
import { useOpenCv } from 'opencv-react';
import type { FreeCropCorner, FreeCropHandle } from '../../types/freeCrop';
import { useFreeCropOpenCv } from './useFreeCropOpenCv';
import {
  CORNER_HANDLE_HIT_SIZE,
  CORNER_HANDLE_SIZE,
  EDGE_HANDLE_HIT_SIZE,
  getCanvasSizeForAspect,
  getImageFrame,
  getScaleToPreview,
} from '../../utils/freeCropUtils';

type UseFreeCropRenderParams = {
  imageUrl: string;
  file: File;
  onChange?: (state: { loading?: boolean }) => void;
};

type UseFreeCropRenderResult = {
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  previewCanvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  loading: boolean;
  naturalSize: { width: number; height: number } | null;
  corners: FreeCropCorner[];
  setCorners: React.Dispatch<React.SetStateAction<FreeCropCorner[]>>;
  showPreview: boolean;
  setShowPreview: React.Dispatch<React.SetStateAction<boolean>>;
  preview: FreeCropHandle['preview'];
  done: FreeCropHandle['done'];
  reset: FreeCropHandle['reset'];
  showPreviewAction: FreeCropHandle['showPreview'];
  hidePreviewAction: FreeCropHandle['hidePreview'];
  saveChanges: FreeCropHandle['saveChanges'];
  revertToInitial: FreeCropHandle['revertToInitial'];
  getSavedImage: FreeCropHandle['getSavedImage'];
  drawPreview: () => void;
  getCornerAtPoint: (x: number, y: number) => number | null;
  getEdgeAtPoint: (x: number, y: number) => number | null;
  clampCorner: (corner: FreeCropCorner, index: number) => FreeCropCorner;
};

export const useFreeCropRender = ({ imageUrl, file, onChange }: UseFreeCropRenderParams): UseFreeCropRenderResult => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const baseCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const { loaded: cvLoaded, cv } = useOpenCv();

  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [corners, setCorners] = useState<FreeCropCorner[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const previewCanvasDataRef = useRef<HTMLCanvasElement | null>(null);
  const initialCornersRef = useRef<FreeCropCorner[]>([]);
  const savedImageBlobRef = useRef<Blob | null>(null);

  const { createBaseMat, cleanupMats, applyPerspectiveTransform } = useFreeCropOpenCv({
    cvLoaded,
    cv,
    baseCanvasRef,
    naturalSize,
    corners,
  });

  const notifyChange = useCallback(
    (isLoading: boolean) => {
      onChange?.({ loading: isLoading });
    },
    [onChange],
  );

  const initializeCorners = useCallback((width: number, height: number) => {
    const initialCorners: FreeCropCorner[] = [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width, y: height },
      { x: 0, y: height },
    ];
    setCorners(initialCorners);
    initialCornersRef.current = initialCorners;
  }, []);

  const getScaleToPreviewValue = useCallback(() => {
    if (!previewCanvasRef.current || !naturalSize) return { scaleX: 1, scaleY: 1 };
    return getScaleToPreview({
      canvasWidth: previewCanvasRef.current.width,
      canvasHeight: previewCanvasRef.current.height,
      naturalWidth: naturalSize.width,
      naturalHeight: naturalSize.height,
    });
  }, [naturalSize]);

  const getImageFrameValue = useCallback(() => {
    if (!previewCanvasRef.current || !naturalSize) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    return getImageFrame({
      canvasWidth: previewCanvasRef.current.width,
      canvasHeight: previewCanvasRef.current.height,
      naturalWidth: naturalSize.width,
      naturalHeight: naturalSize.height,
    });
  }, [naturalSize]);

  const getCornerAtPoint = useCallback(
    (x: number, y: number): number | null => {
      if (!previewCanvasRef.current || !naturalSize) return null;
      const canvas = previewCanvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const canvasX = (x - rect.left) * scaleX;
      const canvasY = (y - rect.top) * scaleY;
      const { scaleX: scaleToPreviewX, scaleY: scaleToPreviewY } = getScaleToPreviewValue();
      const frame = getImageFrameValue();

      for (let i = 0; i < corners.length; i++) {
        const corner = corners[i];
        const previewX = frame.x + corner.x * scaleToPreviewX;
        const previewY = frame.y + corner.y * scaleToPreviewY;
        const dx = canvasX - previewX;
        const dy = canvasY - previewY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < CORNER_HANDLE_HIT_SIZE / 2) {
          return i;
        }
      }
      return null;
    },
    [corners, naturalSize, getScaleToPreviewValue, getImageFrameValue],
  );

  const getEdgeAtPoint = useCallback(
    (x: number, y: number): number | null => {
      if (!previewCanvasRef.current || !naturalSize || corners.length !== 4) return null;
      const canvas = previewCanvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const canvasX = (x - rect.left) * scaleX;
      const canvasY = (y - rect.top) * scaleY;
      const { scaleX: scaleToPreviewX, scaleY: scaleToPreviewY } = getScaleToPreviewValue();
      const frame = getImageFrameValue();
      const scaledCorners = corners.map((corner) => ({
        x: frame.x + corner.x * scaleToPreviewX,
        y: frame.y + corner.y * scaleToPreviewY,
      }));

      let closestEdge: number | null = null;
      let closestDistance = Number.POSITIVE_INFINITY;
      for (let i = 0; i < scaledCorners.length; i++) {
        const start = scaledCorners[i];
        const end = scaledCorners[(i + 1) % scaledCorners.length];
        const segmentX = end.x - start.x;
        const segmentY = end.y - start.y;
        const segmentLengthSquared = segmentX * segmentX + segmentY * segmentY;
        if (segmentLengthSquared === 0) {
          continue;
        }

        const projectedRatio = Math.max(
          0,
          Math.min(1, ((canvasX - start.x) * segmentX + (canvasY - start.y) * segmentY) / segmentLengthSquared),
        );
        const projectedX = start.x + projectedRatio * segmentX;
        const projectedY = start.y + projectedRatio * segmentY;
        const dx = canvasX - projectedX;
        const dy = canvasY - projectedY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < EDGE_HANDLE_HIT_SIZE && distance < closestDistance) {
          closestDistance = distance;
          closestEdge = i;
        }
      }

      return closestEdge;
    },
    [corners, naturalSize, getScaleToPreviewValue, getImageFrameValue],
  );

  const clampCorner = useCallback(
    (corner: FreeCropCorner, _index: number): FreeCropCorner => {
      if (!naturalSize) return corner;
      return {
        x: Math.max(0, Math.min(naturalSize.width, corner.x)),
        y: Math.max(0, Math.min(naturalSize.height, corner.y)),
      };
    },
    [naturalSize],
  );

  const drawPreview = useCallback(() => {
    if (!previewCanvasRef.current || !naturalSize) {
      return;
    }

    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    if (showPreview && previewCanvasDataRef.current) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const previewAspect = previewCanvasDataRef.current.width / previewCanvasDataRef.current.height;
      const canvasAspect = canvas.width / canvas.height;

      let drawWidth = canvas.width;
      let drawHeight = canvas.height;
      let drawX = 0;
      let drawY = 0;

      if (previewAspect > canvasAspect) {
        drawHeight = drawWidth / previewAspect;
        drawY = (canvas.height - drawHeight) / 2;
      } else {
        drawWidth = drawHeight * previewAspect;
        drawX = (canvas.width - drawWidth) / 2;
      }

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(previewCanvasDataRef.current, drawX, drawY, drawWidth, drawHeight);
      return;
    }

    if (baseCanvasRef.current) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const frame = getImageFrameValue();
      ctx.drawImage(baseCanvasRef.current, frame.x, frame.y, frame.width, frame.height);
    }

    if (corners.length === 4) {
      const { scaleX: scaleToPreviewX, scaleY: scaleToPreviewY } = getScaleToPreviewValue();
      const frame = getImageFrameValue();
      const scaledCorners = corners.map((corner) => ({
        x: frame.x + corner.x * scaleToPreviewX,
        y: frame.y + corner.y * scaleToPreviewY,
      }));

      ctx.save();
      ctx.strokeStyle = '#2196F3';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(scaledCorners[0].x, scaledCorners[0].y);
      ctx.lineTo(scaledCorners[1].x, scaledCorners[1].y);
      ctx.lineTo(scaledCorners[2].x, scaledCorners[2].y);
      ctx.lineTo(scaledCorners[3].x, scaledCorners[3].y);
      ctx.closePath();
      ctx.stroke();

      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.beginPath();
      ctx.rect(0, 0, canvas.width, canvas.height);
      ctx.moveTo(scaledCorners[0].x, scaledCorners[0].y);
      ctx.lineTo(scaledCorners[1].x, scaledCorners[1].y);
      ctx.lineTo(scaledCorners[2].x, scaledCorners[2].y);
      ctx.lineTo(scaledCorners[3].x, scaledCorners[3].y);
      ctx.closePath();
      ctx.fill('evenodd');

      scaledCorners.forEach((corner) => {
        ctx.fillStyle = '#2196F3';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(corner.x, corner.y, CORNER_HANDLE_SIZE / 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      });

      ctx.restore();
    }
  }, [corners, naturalSize, getScaleToPreviewValue, getImageFrameValue, showPreview]);

  const preview = useCallback(async () => {
    if (!cvLoaded || !cv || !baseCanvasRef.current || !naturalSize || corners.length !== 4) {
      throw new Error('Free crop tools are not ready');
    }

    const transformedCanvas = document.createElement('canvas');
    applyPerspectiveTransform(transformedCanvas, { quality: 'preview' });

    previewCanvasDataRef.current = transformedCanvas;

    return new Promise<Blob>((resolve, reject) => {
      transformedCanvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create preview'));
          }
        },
        file.type,
        0.95,
      );
    });
  }, [applyPerspectiveTransform, corners, cv, cvLoaded, file.type, naturalSize]);

  const showPreviewAction = useCallback(() => {
    setShowPreview(true);
    if (previewCanvasRef.current) {
      drawPreview();
    }
  }, [drawPreview]);

  const hidePreviewAction = useCallback(() => {
    setShowPreview(false);
    if (previewCanvasRef.current) {
      drawPreview();
    }
  }, [drawPreview]);

  const done = useCallback(async () => {
    if (!cvLoaded || !cv || !baseCanvasRef.current || !naturalSize || corners.length !== 4) {
      throw new Error('Free crop tools are not ready');
    }

    const transformedCanvas = document.createElement('canvas');
    applyPerspectiveTransform(transformedCanvas, { quality: 'full' });

    return new Promise<Blob>((resolve, reject) => {
      transformedCanvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to export free crop'));
          }
        },
        file.type,
        0.95,
      );
    });
  }, [applyPerspectiveTransform, corners, cv, cvLoaded, file.type, naturalSize]);

  const reset = useCallback(() => {
    if (naturalSize) {
      initializeCorners(naturalSize.width, naturalSize.height);
      setShowPreview(false);
      previewCanvasDataRef.current = null;
      savedImageBlobRef.current = null;
      drawPreview();
    }
  }, [drawPreview, initializeCorners, naturalSize]);

  const saveChanges = useCallback(async () => {
    if (!cvLoaded || !cv || !baseCanvasRef.current || !naturalSize || corners.length !== 4) {
      throw new Error('Free crop tools are not ready');
    }

    const transformedCanvas = document.createElement('canvas');
    applyPerspectiveTransform(transformedCanvas, { quality: 'full' });

    return new Promise<Blob>((resolve, reject) => {
      transformedCanvas.toBlob(
        (blob) => {
          if (blob) {
            savedImageBlobRef.current = blob;
            previewCanvasDataRef.current = transformedCanvas;
            initialCornersRef.current = [...corners];
            setShowPreview(true);
            if (previewCanvasRef.current) {
              drawPreview();
            }
            resolve(blob);
          } else {
            reject(new Error('Failed to save changes'));
          }
        },
        file.type,
        0.95,
      );
    });
  }, [applyPerspectiveTransform, corners, cv, cvLoaded, file.type, naturalSize, drawPreview]);

  const getSavedImage = useCallback(() => {
    return savedImageBlobRef.current;
  }, []);

  const revertToInitial = useCallback(() => {
    if (initialCornersRef.current.length === 4) {
      setCorners([...initialCornersRef.current]);
      setShowPreview(false);
      previewCanvasDataRef.current = null;
      drawPreview();
    }
  }, [drawPreview]);

  useEffect(() => {
    return () => {
      cleanupMats();
    };
  }, [cleanupMats]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    notifyChange(true);
    cleanupMats();

    const img = new Image();
    (img as HTMLImageElement).decoding = 'async';
    img.onload = () => {
      if (!mounted) return;

      const baseCanvas = document.createElement('canvas');
      baseCanvas.width = img.naturalWidth || img.width;
      baseCanvas.height = img.naturalHeight || img.height;
      const ctx = baseCanvas.getContext('2d');
      if (!ctx) return;

      ctx.drawImage(img, 0, 0);
      baseCanvasRef.current = baseCanvas;

      if (previewCanvasRef.current && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const { width, height } = getCanvasSizeForAspect(rect.width, rect.height);
        previewCanvasRef.current.width = width;
        previewCanvasRef.current.height = height;
      }

      setNaturalSize({ width: baseCanvas.width, height: baseCanvas.height });
      initializeCorners(baseCanvas.width, baseCanvas.height);
      setLoading(false);
      notifyChange(!cvLoaded);
    };
    img.onerror = () => {
      if (!mounted) return;
      setLoading(false);
      notifyChange(false);
    };
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;

    return () => {
      mounted = false;
    };
  }, [cleanupMats, cvLoaded, imageUrl, initializeCorners, notifyChange]);

  useEffect(() => {
    if (!naturalSize) return;
    if (previewCanvasRef.current && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const canvas = previewCanvasRef.current;
      const { width: canvasWidth, height: canvasHeight } = getCanvasSizeForAspect(rect.width, rect.height);

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      drawPreview();
    }
  }, [naturalSize, drawPreview]);

  useEffect(() => {
    if (!cvLoaded || !naturalSize) {
      return;
    }
    createBaseMat();
    notifyChange(false);
  }, [createBaseMat, cvLoaded, naturalSize, notifyChange]);

  useEffect(() => {
    if (corners.length === 4 && naturalSize) {
      drawPreview();
    }
  }, [corners, naturalSize, drawPreview]);

  useEffect(() => {
    if (showPreview && previewCanvasRef.current) {
      drawPreview();
    }
  }, [showPreview, drawPreview]);

  return {
    containerRef,
    previewCanvasRef,
    loading,
    naturalSize,
    corners,
    setCorners,
    showPreview,
    setShowPreview,
    preview,
    done,
    reset,
    showPreviewAction,
    hidePreviewAction,
    saveChanges,
    revertToInitial,
    getSavedImage,
    drawPreview,
    getCornerAtPoint,
    getEdgeAtPoint,
    clampCorner,
  };
};
